// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * ui.ts — every screen, the HUD, and the touch controls.
 *
 * DOM, not canvas, for all of this: real buttons give real hit targets, focus
 * order and screen-reader labels for free, and the ~44px minimum is something
 * CSS can guarantee rather than something the renderer has to remember.
 *
 * The control scheme is `drag.ts`, not a D-pad (principle #19). Lastlight is a
 * board/tile game — you place things on a map — so a building is dragged from a
 * thumb-height tray onto a hex, with tap-then-tap kept as an equally
 * first-class path because plenty of people prefer it and a drag is fiddly on a
 * small board. Nothing in this game is steered, so there is nothing a stick or
 * a pad would be right for.
 */

import { makeDraggable } from '@ben-gy/game-engine/drag';
import type { Sfx } from '@ben-gy/game-engine/sound';
import { Game, type BuildingKind, type Summary } from './game';
import { COST, MODES, type Mode } from './modes';
import { PALETTE, TERRAIN } from './palette';
import type { Renderer } from './render';
import type { Standing } from './race';

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/**
 * A button that is ALWAYS a `.btn`, whatever else you add to it.
 *
 * The class argument used to replace the default outright, so `button('',
 * 'mode-btn')` produced a button with no `.btn` rule at all — which falls back
 * to the UA button style, a mid-grey #6b6b6b panel. Nothing caught it: the
 * contrast test measures the palette, and the palette was not what painted it.
 * Merging rather than replacing makes that whole class of bug unreachable.
 */
export function button(label: string, className = ''): HTMLButtonElement {
  const classes = new Set(['btn', ...className.split(/\s+/).filter(Boolean)]);
  const b = el('button', [...classes].join(' '), label);
  b.type = 'button';
  return b;
}

const BUILDINGS: ReadonlyArray<{ kind: BuildingKind; name: string; hint: string }> = [
  { kind: 'home', name: 'Hearth-home', hint: 'Shelters 2 · must be in the ring' },
  { kind: 'hut', name: 'Gathering hut', hint: 'Must sit ON the resource it takes' },
  { kind: 'workshop', name: 'Workshop', hint: '+1 to every hut it touches' },
];

// ── legend ────────────────────────────────────────────────────────────────
//
// The reported bug: mid-round there was no way to remind yourself what a tile
// or a mark on the board MEANT without opening the menu — which tears the whole
// round down. So the key lives here, shown over the play view, and it is drawn
// from the same PALETTE/TERRAIN constants the renderer paints with, so a swatch
// can never drift from the colour actually on the map.

type Swatch = { icon: BuildingKind } | { fill: string; shape: 'tile' | 'dot' | 'ring' };

interface LegendEntry {
  swatch: Swatch;
  name: string;
  note: string;
}

interface LegendGroup {
  title: string;
  entries: readonly LegendEntry[];
}

export const LEGEND: readonly LegendGroup[] = [
  {
    title: 'The ground',
    entries: [
      { swatch: { fill: TERRAIN.forest, shape: 'tile' }, name: 'Forest', note: 'Wood — build a gathering hut on it.' },
      { swatch: { fill: TERRAIN.water, shape: 'tile' }, name: 'Water', note: 'Fish — feeds your people each night.' },
      { swatch: { fill: TERRAIN.seam, shape: 'tile' }, name: 'Coal seam', note: 'Coal — the fuel you stoke with.' },
      { swatch: { fill: TERRAIN.snow, shape: 'tile' }, name: 'Snow', note: 'Bare ground. Yields nothing.' },
      { swatch: { fill: PALETTE.fog, shape: 'tile' }, name: 'Fog', note: 'Unexplored. Send a scout to reveal it.' },
      { swatch: { fill: PALETTE.frost, shape: 'tile' }, name: 'Frozen', note: 'The cold took it. Nothing lives here.' },
    ],
  },
  {
    title: 'What you build',
    entries: [
      { swatch: { icon: 'home' }, name: 'Hearth-home', note: 'Shelters 2 — must sit inside the ring.' },
      { swatch: { icon: 'hut' }, name: 'Gathering hut', note: 'Harvests the tile beneath it. Needs a worker.' },
      { swatch: { icon: 'workshop' }, name: 'Workshop', note: '+1 to every hut it touches.' },
    ],
  },
  {
    title: 'On the map',
    entries: [
      { swatch: { fill: PALETTE.generator, shape: 'dot' }, name: 'Generator', note: 'Your only warmth. Tap it to stoke.' },
      { swatch: { fill: PALETTE.ring, shape: 'ring' }, name: 'Warmth ring', note: 'Inside it lives; outside it freezes.' },
      { swatch: { fill: PALETTE.scout, shape: 'dot' }, name: 'Scout', note: 'A worker out in the fog — always a gamble.' },
      { swatch: { fill: PALETTE.cache, shape: 'dot' }, name: 'Cache', note: 'Supplies or a survivor to bring home.' },
    ],
  },
];

/**
 * Build the legend card as a standalone element. Pure and export-visible so a
 * test can assert the key names every terrain, building and mark the game draws
 * — the same "the constants must actually reach the screen" guard the rest of
 * this file is built around.
 */
export function buildLegendCard(): HTMLElement {
  const card = el('div', 'legend-card');
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-modal', 'true');
  card.setAttribute('aria-label', 'Legend');

  for (const group of LEGEND) {
    const g = el('div', 'legend-group');
    g.appendChild(el('h3', 'legend-title', group.title));
    for (const entry of group.entries) {
      const row = el('div', 'legend-row');
      const swatch = el('span', 'legend-swatch');
      if ('icon' in entry.swatch) {
        swatch.classList.add('tray-icon', `icon-${entry.swatch.icon}`);
      } else {
        swatch.classList.add(entry.swatch.shape);
        // The renderer paints a ring as a stroke, so the swatch is an outline;
        // everything else is a fill.
        if (entry.swatch.shape === 'ring') swatch.style.borderColor = entry.swatch.fill;
        else swatch.style.background = entry.swatch.fill;
      }
      const text = el('div', 'legend-text');
      text.append(el('span', 'legend-name', entry.name), el('span', 'legend-note', entry.note));
      row.append(swatch, text);
      g.appendChild(row);
    }
    card.appendChild(g);
  }
  return card;
}

// ── shell ───────────────────────────────────────────────────────────────────

export interface Shell {
  view: HTMLElement;
  footer: HTMLElement;
}

export function mountShell(root: HTMLElement): Shell {
  root.innerHTML = '';
  const main = el('div', 'main-content');
  const view = el('div', 'view');
  main.appendChild(view);

  const footer = el('footer', 'site-footer');
  const inner = el('div', 'footer-inner');
  inner.innerHTML =
    'Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · ' +
    '<a href="https://hub.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>';
  footer.appendChild(inner);

  root.appendChild(main);
  root.appendChild(footer);
  return { view, footer };
}

// ── menu ────────────────────────────────────────────────────────────────────

export interface MenuConfig {
  mode: Mode;
  best: number;
  muted: boolean;
  onMode: (mode: Mode) => void;
  onSolo: () => void;
  onDaily: () => void;
  onFriends: () => void;
  onHelp: () => void;
  onAbout: () => void;
  onMute: (muted: boolean) => void;
}

export function renderMenu(view: HTMLElement, cfg: MenuConfig): void {
  view.innerHTML = '';
  const wrap = el('div', 'menu');

  const title = el('h1', 'title', 'Lastlight');
  const tag = el('p', 'tagline', 'One generator against the cold. Everything outside the ring freezes.');
  wrap.append(title, tag);

  const modes = el('div', 'modes');
  modes.setAttribute('role', 'group');
  modes.setAttribute('aria-label', 'Choose a mode');
  for (const m of MODES) {
    // 'btn mode-btn', never just 'mode-btn': the class argument REPLACES the
    // default, so dropping `btn` silently falls back to the UA button style —
    // a mid-grey #6b6b6b panel that no palette test covers, because the palette
    // was never what painted it.
    const b = button('', 'btn mode-btn');
    b.classList.toggle('on', m.id === cfg.mode.id);
    b.setAttribute('aria-pressed', String(m.id === cfg.mode.id));
    b.append(
      el('span', 'mode-name', m.name),
      el('span', 'mode-meta', `${m.days} days · map ${m.radius} · ring ${Math.floor(m.startHeat)}`),
      el('span', 'mode-blurb', m.blurb),
    );
    b.addEventListener('click', () => cfg.onMode(m));
    modes.appendChild(b);
  }
  wrap.appendChild(modes);

  const play = button('Play', 'btn primary big');
  play.addEventListener('click', cfg.onSolo);
  wrap.appendChild(play);

  const row = el('div', 'menu-row');
  const daily = button("Today's map", 'btn');
  daily.addEventListener('click', cfg.onDaily);
  const friends = button('Play with friends', 'btn');
  friends.addEventListener('click', cfg.onFriends);
  row.append(daily, friends);
  wrap.appendChild(row);

  if (cfg.best > 0) {
    wrap.appendChild(el('p', 'best', `Your best on ${cfg.mode.name}: ${cfg.best}`));
  }

  const row2 = el('div', 'menu-row');
  const help = button('How to play', 'btn ghost');
  help.addEventListener('click', cfg.onHelp);
  const about = button('About', 'btn ghost');
  about.addEventListener('click', cfg.onAbout);
  const mute = button(cfg.muted ? 'Sound off' : 'Sound on', 'btn ghost');
  mute.setAttribute('aria-pressed', String(cfg.muted));
  mute.addEventListener('click', () => cfg.onMute(!cfg.muted));
  row2.append(help, about, mute);
  wrap.appendChild(row2);

  view.appendChild(wrap);
}

// ── panels ──────────────────────────────────────────────────────────────────

export function renderHelp(view: HTMLElement, onBack: () => void): void {
  view.innerHTML = '';
  const wrap = el('div', 'panel-screen');
  wrap.appendChild(el('h2', 'panel-title', 'How to play'));
  const body = el('div', 'panel-body');
  body.innerHTML = `
    <p>Your generator is the only warmth left. Everything inside the glowing ring lives;
       everything outside it freezes.</p>
    <p><strong>Drag a building</strong> from the tray onto the map (or tap the building, then tap a tile):</p>
    <ul>
      <li><strong>Hearth-home</strong> — shelters 2 people. Anyone without a place inside the ring
          freezes at nightfall.</li>
      <li><strong>Gathering hut</strong> — must sit <em>on</em> the tile it harvests: forest for wood,
          water for fish, a seam for coal. It needs a worker, and it must be inside the ring.</li>
      <li><strong>Workshop</strong> — adds +1 to every hut it physically touches.</li>
    </ul>
    <p>Every night the cold eats into your heat and the ring closes — and a <em>bigger</em> ring
       costs more to hold. <strong>Stoke</strong> the generator to push it back out, feed everyone a
       fish, and send <strong>scouts</strong> into the fog for caches and survivors. A scout is a
       worker who is not working, so it is always a gamble.</p>
    <p>Get your people through every night and the last light is still yours.</p>`;
  wrap.appendChild(body);
  const back = button('Back', 'btn primary');
  back.addEventListener('click', onBack);
  wrap.appendChild(back);
  view.appendChild(wrap);
}

export function renderAbout(view: HTMLElement, onBack: () => void): void {
  view.innerHTML = '';
  const wrap = el('div', 'panel-screen');
  wrap.appendChild(el('h2', 'panel-title', 'About'));
  const body = el('div', 'panel-body');
  body.innerHTML = `
    <p><strong>Lastlight</strong> is a small frozen-frontier settlement game: one generator, a
       closing ring of warmth, and a handful of days to get your people through.</p>
    <p>It runs entirely in your browser. No account, no cookies, no tracking, no third-party fonts.
       Page views are counted anonymously with Cloudflare Web Analytics.</p>
    <p><strong>Playing with friends</strong> connects your browsers directly to each other
       (peer-to-peer WebRTC). A public signalling relay is used only to introduce the browsers to
       one another; no game state is ever stored on a server, and there is no server to store it on.
       Because it is a direct connection, the people in your room can see your IP address — the same
       as any video call — so play with people you know.</p>
    <p>Everyone in a room races the <em>same</em> map from the same seed. Nobody gets a warmer
       start.</p>
    <p>Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>.</p>`;
  wrap.appendChild(body);
  const back = button('Back', 'btn primary');
  back.addEventListener('click', onBack);
  wrap.appendChild(back);
  view.appendChild(wrap);
}

// ── results ─────────────────────────────────────────────────────────────────

export interface ResultsConfig {
  mode: Mode;
  summary: Summary;
  best: number;
  /** Empty for a solo run. */
  standings: Standing[];
  /** Null when the ladder is still open. */
  closesInMs: number | null;
  multiplayer: boolean;
  votes: string;
  startsInMs: number | null;
  canForceStart: boolean;
  onAgain: () => void;
  onLobby: () => void;
  onMenu: () => void;
  onShare: () => void;
  onForceStart: () => void;
  onFeedback: (from: HTMLElement) => void;
}

export function renderResults(view: HTMLElement, cfg: ResultsConfig): void {
  view.innerHTML = '';
  const wrap = el('div', 'panel-screen results');
  const s = cfg.summary;

  wrap.appendChild(el('h2', 'panel-title', s.won ? 'The light held' : 'The light went out'));
  wrap.appendChild(el('p', 'results-reason', s.reason));

  const grid = el('div', 'score-grid');
  for (const [label, value] of [
    ['Score', String(s.score)],
    ['Survivors', String(s.survivors)],
    ['Buildings', String(s.buildings)],
    ['Caches', String(s.caches)],
    ['Nights', `${s.days}/${cfg.mode.days}`],
    ['Best', String(Math.max(cfg.best, s.score))],
  ]) {
    const cell = el('div', 'score-cell');
    cell.append(el('span', 'score-value', value), el('span', 'score-label', label));
    grid.appendChild(cell);
  }
  wrap.appendChild(grid);

  // Principle #9: everyone's result, every time — and a per-player BREAKDOWN,
  // not just a name and a number. In a same-seed race this is the whole point:
  // you all had the identical map, so the differences are yours.
  if (cfg.multiplayer && cfg.standings.length) {
    wrap.appendChild(el('h3', 'sub-title', 'Everyone on this map'));
    const table = el('div', 'ladder');
    cfg.standings.forEach((p, i) => {
      const row = el('div', 'ladder-row');
      if (p.isSelf) row.classList.add('me');
      row.append(el('span', 'lr-rank', String(i + 1)), el('span', 'lr-name', p.name));
      if (p.result) {
        const r = p.result;
        row.append(
          el('span', 'lr-score', String(r.score)),
          el(
            'span',
            'lr-detail',
            `${r.survivors} survived · ${r.buildings} built · ${r.caches} caches · night ${r.days}`,
          ),
        );
      } else {
        row.append(
          el('span', 'lr-score', p.gone ? '—' : '…'),
          el('span', 'lr-detail', p.gone ? 'left the room' : `still out there — night ${p.day}`),
        );
      }
      table.appendChild(row);
    });
    wrap.appendChild(table);

    if (cfg.closesInMs !== null) {
      wrap.appendChild(
        el(
          'p',
          'waiting-note',
          `Waiting for the others — results close in ${Math.ceil(cfg.closesInMs / 1000)}s.`,
        ),
      );
    }
  }

  const actions = el('div', 'menu-row');
  if (cfg.multiplayer) {
    const again = button('Play again', 'btn primary');
    again.addEventListener('click', cfg.onAgain);
    actions.appendChild(again);
    const lobby = button('Back to lobby', 'btn');
    lobby.addEventListener('click', cfg.onLobby);
    actions.appendChild(lobby);
  } else {
    const again = button('Play again', 'btn primary');
    again.addEventListener('click', cfg.onAgain);
    const share = button('Share this map', 'btn');
    share.addEventListener('click', cfg.onShare);
    actions.append(again, share);
  }
  const menu = button('Menu', 'btn ghost');
  menu.addEventListener('click', cfg.onMenu);
  actions.appendChild(menu);
  wrap.appendChild(actions);

  if (cfg.multiplayer) {
    // Every waiting state gets a reason AND a horizon (principle #12).
    const note = el('p', 'waiting-note', cfg.votes);
    wrap.appendChild(note);
    if (cfg.startsInMs !== null) {
      wrap.appendChild(
        el('p', 'waiting-note', `Next run starts in ${Math.ceil(cfg.startsInMs / 1000)}s.`),
      );
    }
    if (cfg.canForceStart) {
      const force = button('Start now', 'btn ghost');
      force.addEventListener('click', cfg.onForceStart);
      wrap.appendChild(force);
    }
  }

  // Feedback is wired here as well as in the footer, because `body.playing`
  // hides the footer for the whole round — exactly when a player hits the bug
  // they want to report.
  const fb = button('Something wrong? Tell me', 'btn link results-feedback');
  fb.addEventListener('click', () => cfg.onFeedback(fb));
  wrap.appendChild(fb);

  view.appendChild(wrap);
}

// ── the play view ───────────────────────────────────────────────────────────

export interface PlayConfig {
  game: () => Game;
  renderer: () => Renderer;
  sfx: Sfx;
  multiplayer: boolean;
  onBuild: (kind: BuildingKind, q: number, r: number) => void;
  onStoke: () => void;
  onScout: (q: number, r: number) => void;
  onEndDay: () => void;
  onMenu: () => void;
}

export interface PlayView {
  root: HTMLElement;
  canvas: HTMLCanvasElement;
  refresh(): void;
  setStandings(list: Standing[]): void;
  toast(text: string, tone?: 'good' | 'bad'): void;
  destroy(): void;
}

export function createPlayView(cfg: PlayConfig): PlayView {
  const root = el('div', 'play');

  // ── HUD ──
  const hud = el('div', 'hud');
  const dayEl = el('span', 'stat-value');
  const heatEl = el('span', 'stat-value');
  const popEl = el('span', 'stat-value');
  const woodEl = el('span', 'stat-value');
  const fishEl = el('span', 'stat-value');
  const coalEl = el('span', 'stat-value');

  const stat = (label: string, value: HTMLElement, cls = ''): HTMLElement => {
    const s = el('div', `stat ${cls}`.trim());
    s.append(value, el('span', 'stat-label', label));
    return s;
  };
  hud.append(
    stat('Night', dayEl),
    stat('Ring', heatEl, 'heat'),
    stat('People', popEl),
    stat('Wood', woodEl),
    stat('Fish', fishEl),
    stat('Coal', coalEl),
  );

  const helpBtn = button('?', 'btn icon hud-help');
  helpBtn.setAttribute('aria-label', 'Legend');
  hud.appendChild(helpBtn);

  const menuBtn = button('☰', 'btn icon hud-menu');
  menuBtn.setAttribute('aria-label', 'Menu');
  menuBtn.addEventListener('click', cfg.onMenu);
  hud.appendChild(menuBtn);
  root.appendChild(hud);

  // ── legend overlay ──
  // Shown OVER the running round, never in place of it: reaching the key must
  // not cost you the game. The map keeps animating behind the scrim.
  const legend = el('div', 'legend-overlay');
  legend.hidden = true;
  const legendCard = buildLegendCard();
  const legendClose = button('✕', 'btn icon legend-close');
  legendClose.setAttribute('aria-label', 'Close legend');
  legendCard.insertBefore(legendClose, legendCard.firstChild);
  legend.appendChild(legendCard);
  root.appendChild(legend);

  let legendOpen = false;
  const openLegend = (): void => {
    legendOpen = true;
    legend.hidden = false;
    cfg.sfx.play('select');
    legendClose.focus();
  };
  const closeLegend = (): void => {
    legendOpen = false;
    legend.hidden = true;
    helpBtn.focus();
  };
  helpBtn.addEventListener('click', openLegend);
  legendClose.addEventListener('click', closeLegend);
  // A tap on the scrim (but not the card) closes it, like every other overlay.
  legend.addEventListener('pointerup', (e) => {
    if (e.target === legend) closeLegend();
  });

  const strip = el('div', 'standings');
  strip.hidden = !cfg.multiplayer;
  root.appendChild(strip);

  // ── map ──
  const stage = el('div', 'stage');
  const canvas = el('canvas', 'map');
  canvas.setAttribute('role', 'img');
  canvas.setAttribute('aria-label', 'The settlement map');
  stage.appendChild(canvas);
  const toasts = el('div', 'toasts');
  stage.appendChild(toasts);
  root.appendChild(stage);

  // ── tray ──
  const tray = el('div', 'tray');
  const trayRow = el('div', 'tray-row');
  let selected: BuildingKind | null = null;
  let scouting = false;

  const trayBtns = new Map<BuildingKind, HTMLButtonElement>();
  for (const b of BUILDINGS) {
    const btn = button('', 'btn tray-btn');
    btn.dataset.kind = b.kind;
    btn.setAttribute('aria-label', `${b.name}, ${COST[b.kind]} wood. ${b.hint}`);
    btn.append(
      el('span', `tray-icon icon-${b.kind}`),
      el('span', 'tray-name', b.name.replace('-', '‑')),
      el('span', 'tray-cost', `${COST[b.kind]} wood`),
    );
    trayRow.appendChild(btn);
    trayBtns.set(b.kind, btn);
  }
  tray.appendChild(trayRow);

  const actions = el('div', 'tray-actions');
  const stokeBtn = button('', 'btn stoke');
  const scoutBtn = button('Send scout', 'btn');
  const endBtn = button('End day', 'btn primary');
  actions.append(stokeBtn, scoutBtn, endBtn);
  tray.appendChild(actions);
  root.appendChild(tray);

  // ── selection + placement ──

  const setSelected = (kind: BuildingKind | null): void => {
    selected = kind;
    if (kind) scouting = false;
    for (const [k, btn] of trayBtns) {
      const on = k === kind;
      btn.classList.toggle('on', on);
      btn.setAttribute('aria-pressed', String(on));
    }
    scoutBtn.classList.toggle('on', scouting);
    scoutBtn.setAttribute('aria-pressed', String(scouting));
    cfg.renderer().setHover(null, false);
  };

  const setScouting = (on: boolean): void => {
    scouting = on;
    if (on) setSelected(null);
    scoutBtn.classList.toggle('on', on);
    scoutBtn.setAttribute('aria-pressed', String(on));
  };

  // Ghost that follows the finger during a drag, so the thing being placed is
  // never hidden under the thumb.
  let ghost: HTMLElement | null = null;
  const killGhost = (): void => {
    ghost?.remove();
    ghost = null;
  };

  const draggables = BUILDINGS.map((b) => {
    const btn = trayBtns.get(b.kind)!;
    let dragging = false;
    let originX = 0;
    let originY = 0;

    return makeDraggable(btn, {
      onTap: () => {
        cfg.sfx.play('select');
        setSelected(selected === b.kind ? null : b.kind);
      },
      onDragStart: (e) => {
        dragging = true;
        originX = e.clientX;
        originY = e.clientY;
        setSelected(b.kind);
        ghost = el('div', `drag-ghost icon-${b.kind}`);
        document.body.appendChild(ghost);
        ghost.style.left = `${originX}px`;
        ghost.style.top = `${originY}px`;
      },
      onDragMove: (dx, dy) => {
        if (!dragging) return;
        const x = originX + dx;
        const y = originY + dy;
        if (ghost) {
          ghost.style.left = `${x}px`;
          ghost.style.top = `${y}px`;
        }
        const cell = cfg.renderer().cellAt(x, y);
        cfg.renderer().setHover(cell, cell ? cfg.game().canBuild(b.kind, cell.q, cell.r) : false);
      },
      onDrop: (dx, dy) => {
        dragging = false;
        killGhost();
        const cell = cfg.renderer().cellAt(originX + dx, originY + dy);
        cfg.renderer().setHover(null, false);
        if (cell) cfg.onBuild(b.kind, cell.q, cell.r);
        setSelected(null);
      },
      onCancel: () => {
        // A pointercancel is an aborted gesture (a call, a notification), never
        // a placement.
        dragging = false;
        killGhost();
        cfg.renderer().setHover(null, false);
        setSelected(null);
      },
    });
  });

  // Tap-to-place and tap-the-generator, on the map itself.
  const onMapPointer = (e: PointerEvent): void => {
    const cell = cfg.renderer().cellAt(e.clientX, e.clientY);
    if (!cell) return;
    if (scouting) {
      cfg.onScout(cell.q, cell.r);
      setScouting(false);
      return;
    }
    if (selected) {
      cfg.onBuild(selected, cell.q, cell.r);
      setSelected(null);
      return;
    }
    if (cell.q === 0 && cell.r === 0) cfg.onStoke();
  };
  canvas.addEventListener('pointerup', onMapPointer);

  const onMapHover = (e: PointerEvent): void => {
    if (!selected && !scouting) return;
    const cell = cfg.renderer().cellAt(e.clientX, e.clientY);
    if (!cell) return cfg.renderer().setHover(null, false);
    const ok = scouting
      ? cfg.game().canScout(cell.q, cell.r)
      : cfg.game().canBuild(selected!, cell.q, cell.r);
    cfg.renderer().setHover(cell, ok);
  };
  canvas.addEventListener('pointermove', onMapHover);

  stokeBtn.addEventListener('click', cfg.onStoke);
  scoutBtn.addEventListener('click', () => setScouting(!scouting));
  endBtn.addEventListener('click', () => {
    setSelected(null);
    setScouting(false);
    cfg.onEndDay();
  });

  // Keyboard, for desktop.
  const onKey = (e: KeyboardEvent): void => {
    if (e.target instanceof HTMLInputElement) return;
    // While the legend is up it owns the keyboard: Escape closes it rather than
    // clearing a selection on the board underneath.
    if (legendOpen) {
      if (e.key === 'Escape') closeLegend();
      return;
    }
    if (e.key === '1') setSelected('home');
    else if (e.key === '2') setSelected('hut');
    else if (e.key === '3') setSelected('workshop');
    else if (e.key.toLowerCase() === 's') cfg.onStoke();
    else if (e.key.toLowerCase() === 'e') cfg.onEndDay();
    else if (e.key === 'Escape') {
      setSelected(null);
      setScouting(false);
    }
  };
  window.addEventListener('keydown', onKey);

  function refresh(): void {
    const g = cfg.game();
    dayEl.textContent = `${g.day}/${g.mode.days}`;
    heatEl.textContent = `${g.warmth}`;
    heatEl.title = `Heat ${g.heat.toFixed(2)}`;
    popEl.textContent = `${g.pop}/${g.shelterSlots}`;
    popEl.classList.toggle('warn', g.pop > g.shelterSlots);
    woodEl.textContent = String(g.wood);
    fishEl.textContent = String(g.fish);
    fishEl.classList.toggle('warn', g.fish < g.pop);
    coalEl.textContent = String(g.coal);

    stokeBtn.textContent = `Stoke (${COST.stokeCoal} coal)`;
    stokeBtn.disabled = !g.canStoke();
    scoutBtn.disabled = g.workers <= 0;

    for (const [kind, btn] of trayBtns) {
      const affordable = g.wood >= COST[kind];
      btn.classList.toggle('poor', !affordable);
      btn.disabled = g.over;
    }
    endBtn.disabled = g.over;
  }

  function setStandings(list: Standing[]): void {
    if (!cfg.multiplayer) return;
    strip.innerHTML = '';
    for (const p of list) {
      const chip = el('div', 'chip');
      if (p.isSelf) chip.classList.add('me');
      if (p.done) chip.classList.add('done');
      if (p.gone) chip.classList.add('gone');
      chip.append(
        el('span', 'chip-name', p.name),
        el('span', 'chip-score', p.gone ? '—' : String(p.score)),
        el('span', 'chip-day', p.gone ? 'left' : p.done ? 'done' : `n${p.day}`),
      );
      strip.appendChild(chip);
    }
  }

  function toast(text: string, tone?: 'good' | 'bad'): void {
    const t = el('div', `toast${tone ? ` ${tone}` : ''}`, text);
    toasts.appendChild(t);
    setTimeout(() => t.classList.add('out'), 1800);
    setTimeout(() => t.remove(), 2400);
  }

  return {
    root,
    canvas,
    refresh,
    setStandings,
    toast,
    destroy(): void {
      window.removeEventListener('keydown', onKey);
      canvas.removeEventListener('pointerup', onMapPointer);
      canvas.removeEventListener('pointermove', onMapHover);
      for (const d of draggables) d.destroy();
      killGhost();
    },
  };
}

/** Colour for a floating number, kept out of the renderer so the palette owns it. */
export const FLOAT_COLOURS = {
  wood: PALETTE.hut,
  fish: PALETTE.scout,
  coal: PALETTE.workshop,
  bad: PALETTE.danger,
  good: PALETTE.good,
} as const;
